if (!customElements.get('mio-mill-pair')) {
  customElements.define(
    'mio-mill-pair',
    class extends HTMLElement {
      connectedCallback() {
        this.host = this.closest('product-info');
        this.form = document.getElementById(`product-form-${this.dataset.section}`);
        if (!this.host || !this.form) return;
        this.events = new AbortController();
        this.mode = 'unit';
        this.productTitle = this.host.querySelector('[data-mill-product-title]');
        this.unitTitle = this.productTitle?.textContent;
        this.inputs = [...this.querySelectorAll('[data-mill]')];
        const landingParams = new URLSearchParams(window.location.search);
        if (landingParams.get('pair') === '1') {
          ['salt', 'pepper'].forEach((kind) => {
            const requested = landingParams.get(kind);
            const input = this.inputs.find(
              (candidate) =>
                candidate.dataset.mill === kind &&
                candidate.value === requested &&
                candidate.dataset.available === 'true',
            );
            if (input) input.checked = true;
          });
        }
        this.gallery = this.host.querySelector('mio-gallery');
        const priceContainer = this.host.querySelector(`[id="price-${this.dataset.section}"]`);
        this.unitPrice = priceContainer?.querySelector('[data-mill-unit-price]');
        if (this.unitPrice) {
          this.pairPrice = this.querySelector('[data-pair-price-template]').content.firstElementChild.cloneNode(true);
          priceContainer.append(this.pairPrice);
        }
        this.querySelectorAll('[data-mode]').forEach((button) => {
          button.disabled = button.dataset.mode === 'pair' && !this.validSelection();
          button.addEventListener('click', () => this.setMode(button.dataset.mode), { signal: this.events.signal });
        });
        this.inputs.forEach((input) => {
          input.addEventListener(
            'change',
            () => {
              if (this.mode === 'unit') {
                this.openSingle(input);
                return;
              }
              this.refresh();
              this.preview();
            },
            { signal: this.events.signal },
          );
          input.addEventListener(
            'click',
            () => {
              if (this.mode === 'pair' && input.checked) this.preview();
            },
            { signal: this.events.signal },
          );
        });
        this.host.classList.add('mio-is-mill-picker');
        this.querySelector('[data-primary-row]').hidden = false;
        const requestedMode = { 1: 'pair', 0: 'unit' }[landingParams.get('pair')];
        // Open on the pair only when the same-color partner is in stock, not on a substitute color.
        const defaultMode = this.dataset.partnerMatch === 'false' ? 'unit' : this.dataset.defaultMode;
        this.setMode(requestedMode || defaultMode || 'unit', { track: requestedMode === 'pair' });
        this.observeOffer();
      }

      disconnectedCallback() {
        this.events?.abort();
        this.offerObserver?.disconnect();
        this.host?.classList.remove('mio-is-mill-picker', 'mio-is-pair-mode');
        this.gallery?.clearSelectionPreview?.();
        this.pairPrice?.remove();
        if (this.productTitle) this.productTitle.textContent = this.unitTitle;
        if (this.unitPrice) this.unitPrice.hidden = false;
        this.updateDescription(false, false);
      }

      selected() {
        return this.inputs.filter((input) => input.checked);
      }

      validSelection() {
        const inputs = this.selected();
        return (
          inputs.length === 2 &&
          inputs.every((input) => input.dataset.available === 'true') &&
          inputs[0].value !== inputs[1].value
        );
      }

      observeOffer() {
        if (!window.mioClarity || !('IntersectionObserver' in window)) return;
        const recordView = () => {
          if (!this.offerInView || document.visibilityState !== 'visible') return;
          window.mioClarity?.eventOnce('mio_mill_pair_viewed');
          this.offerObserver.disconnect();
        };
        this.offerObserver = new IntersectionObserver(
          ([entry]) => {
            this.offerInView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
            recordView();
          },
          { threshold: 0.5 },
        );
        this.offerObserver.observe(this.querySelector('[data-mode="pair"]'));
        document.addEventListener('visibilitychange', recordView, { signal: this.events.signal });
      }

      setMode(mode, { track = true } = {}) {
        if (this.form.querySelector('[name="add"]')?.getAttribute('aria-disabled') === 'true') return;
        if (mode === 'pair' && !this.validSelection()) mode = 'unit';
        const primary = this.selected().find((input) => input.dataset.mill === this.dataset.primaryKind);
        if (mode === 'unit' && primary?.value !== this.dataset.currentVariant) {
          this.openSingle(primary);
          return;
        }
        this.mode = mode;
        const paired = mode === 'pair';
        this.host.classList.toggle('mio-is-pair-mode', paired);
        this.querySelector('[data-pair-fields]').hidden = !paired;
        this.querySelector('[data-unit-note]').hidden = paired;
        this.inputs.forEach((input) => {
          input.disabled =
            input.dataset.available !== 'true' || (!paired && input.dataset.mill !== this.dataset.primaryKind);
        });
        this.querySelectorAll('[data-mode]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.mode === mode));
        });
        if (!paired) this.gallery?.clearSelectionPreview?.();
        this.refresh();
        if (paired) this.preview();
        if (paired && track) window.mioClarity?.eventOnce('mio_mill_pair_selected');
      }

      openSingle(input) {
        if (!input) return;
        const url = new URL(input.dataset.url, window.location.origin);
        new URLSearchParams(window.location.search).forEach((value, key) => {
          if (!['pair', 'variant', 'salt', 'pepper'].includes(key)) url.searchParams.set(key, value);
        });
        // Pages open in pair mode by default; keep a shopper who chose one mill in unit mode.
        if (this.dataset.defaultMode === 'pair') url.searchParams.set('pair', '0');
        window.location.assign(url.href);
      }

      preview() {
        const mills = this.selected().map((input) => ({
          src: input.dataset.image,
          alt: input.dataset.title,
          kind: input.dataset.mill,
        }));
        this.gallery?.showSelectionPreview?.(mills);
      }

      refresh() {
        const inputs = this.selected();
        inputs.forEach((input) => {
          this.querySelector(`[data-selected-color="${input.dataset.mill}"]`).textContent = input.dataset.color;
        });
        const full = inputs.reduce((sum, input) => sum + Number(input.dataset.price), 0);
        const percent = Number(this.dataset.percent);
        const total = inputs.reduce((sum, input) => {
          const price = Number(input.dataset.price);
          return sum + price - Math.round((price * percent) / 100);
        }, 0);
        const primary = inputs.find((input) => input.dataset.mill === this.dataset.primaryKind);
        const summary = inputs.map((input) => input.dataset.title).join(' + ');
        this.querySelector('[data-pair-summary]').textContent = summary;
        this.querySelector('[data-pair-full]').textContent = this.money(full);
        this.querySelector('[data-pair-total]').textContent = this.money(total);
        this.querySelector('[data-unit-price]').textContent = this.money(Number(primary?.dataset.price || 0));
        this.querySelector('[data-pair-error]').hidden = true;
        const paired = this.mode === 'pair';
        if (this.productTitle) this.productTitle.textContent = paired ? this.dataset.pairTitle : this.unitTitle;
        if (this.pairPrice) {
          this.pairPrice.querySelector('[data-current-pair-summary]').textContent = summary;
          this.pairPrice.querySelector('[data-current-pair-price]').textContent = this.money(total);
          this.pairPrice.querySelector('[data-current-pair-full]').textContent = this.money(full);
          this.unitPrice.hidden = paired;
          this.pairPrice.hidden = !paired;
        }
        this.updateDescription(paired, this.validSelection());
        const label = paired ? this.dataset.addLabel : this.dataset.unitLabel;
        const buttonText = this.form.querySelector('.mio-btn-text');
        if (buttonText) buttonText.textContent = label;
        this.dispatchEvent(
          new CustomEvent('mio:purchase-change', {
            bubbles: true,
            detail: {
              formId: this.form.getAttribute('id'),
              label,
              price: this.money(paired ? total : Number(primary?.dataset.price || 0)),
            },
          }),
        );
      }

      updateDescription(paired, pickerAvailable) {
        if (!this.host) return;
        const visibility = {
          'data-mill-unit-copy': !paired,
          'data-mill-pair-copy': paired,
          'data-mill-legacy-guidance': !pickerAvailable,
          'data-mill-picker-guidance': pickerAvailable,
        };
        Object.entries(visibility).forEach(([attribute, visible]) => {
          this.host.querySelectorAll(`[${attribute}]`).forEach((node) => {
            node.hidden = !visible;
          });
        });
      }

      money(cents) {
        return window.mioFormatMoney(cents);
      }

      prepareFormData(formData) {
        if (this.mode !== 'pair') return true;
        if (!this.validSelection()) {
          const error = this.querySelector('[data-pair-error]');
          error.textContent = this.dataset.error;
          error.hidden = false;
          window.mioClarity?.trackMillAddError('pair', 'selection');
          return false;
        }
        formData.delete('id');
        formData.delete('quantity');
        this.selected().forEach((input, index) => {
          formData.append(`items[${index}][id]`, input.value);
          formData.append(`items[${index}][quantity]`, '1');
        });
        return true;
      }
    },
  );
}
